import ContextualHint, { HintId } from "../onboarding/hints/ContextualHint";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { MouseEvent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  addInteractionCopy,
  addInteractionToQuizFeedback,
  selectSelectedElementId,
  selectSelectedInteractionIds,
  selectTrainingType,
  setPrimaryInteraction,
  setSelectedInteraction,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import {
  getFeedbackInteractionDndId,
  getFeedbackListDndId,
  TakeInteractionDndData,
  useTakeInteractionDndContext,
} from "../training_timeline/TakeInteractionDndProvider";
import Interaction from "../../models/interactions/Interaction";
import { IFormQuiz, QuizFeedbackSlot } from "../../models/Quiz";
import { getFeedbackArray, isQuizFeedbackEnabled } from "../../models/QuizFeedbackHelper";
import EditorColumn, {
  EditorColumnHeader,
  EditorColumnInlineAddButton,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import EditorInteractionRow from "../editor_columns/EditorInteractionRow";
import {
  FeedbackSelectableDefinitions360,
  FeedbackSelectableDefinitions3D,
  InteractionDefintion,
} from "../interaction_picker/InteractionDefinitions";
import InteractionContextMenu from "../interaction_context_menu/InteractionContextMenu";
import InteractionPicker from "../interaction_picker/InteractionPicker";
import useQuizFeedbackColumn from "./QuizFeedbackColumn.hook";

function QuizFeedbackSection(props: {
  quiz: IFormQuiz;
  takeId: string;
  slot: QuizFeedbackSlot;
  title: string;
  icon: ReactNode;
  definitions: InteractionDefintion[];
  onAddInteraction: () => void;
}) {
  const { quiz, takeId, slot, title, icon, definitions, onAddInteraction } =
    props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedElementId = useSelector(selectSelectedElementId);
  const selectedInteractionIds = useSelector(selectSelectedInteractionIds);
  const interactions = getFeedbackArray(quiz, slot);
  const { blockedDropTarget, hoverFeedbackSlot } =
    useTakeInteractionDndContext();
  const isBlockedDropTarget =
    blockedDropTarget?.quizId === quiz.id && blockedDropTarget.slot === slot;
  const isHoverDropTarget =
    hoverFeedbackSlot?.quizId === quiz.id && hoverFeedbackSlot.slot === slot;
  const { setNodeRef } = useDroppable({
    id: getFeedbackListDndId(quiz.id, slot),
    data: {
      kind: "feedback-list",
      takeId,
      quizId: quiz.id,
      slot,
    } as TakeInteractionDndData,
  });

  const getInteractionIndex = (interactionId: string) =>
    interactions.findIndex((interaction) => interaction.id === interactionId);

  // right-clicking a row selects it so the quick-add menu inserts after it;
  // a row that is part of the multi-selection keeps the group intact
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-interaction-id]"
    );
    const interactionId = row?.dataset.interactionId;
    if (!interactionId) return;
    dispatch(
      selectedInteractionIds.includes(interactionId)
        ? setPrimaryInteraction(interactionId)
        : setSelectedInteraction(interactionId)
    );
  };

  const addFromContextMenu = (interaction: Interaction) => {
    const selectedIndex = selectedElementId
      ? getInteractionIndex(selectedElementId)
      : -1;
    if (selectedIndex !== -1) {
      // a row in this slot is selected: insert the new interaction after it
      dispatch(addInteractionCopy(interaction));
    } else {
      dispatch(
        addInteractionToQuizFeedback({ quizId: quiz.id, slot, interaction })
      );
    }
  };

  return (
    <InteractionContextMenu
      definitions={definitions}
      onAddInteraction={addFromContextMenu}
      pasteDestination={{ quizId: quiz.id, slot }}
    >
      <div onContextMenu={handleContextMenu}>
        <EditorColumnSection
          title={title}
          icon={icon}
          defaultExpanded
          testId={`quiz-feedback-section-${slot}`}
        >
          <SortableContext
            items={interactions.map((interaction) =>
              getFeedbackInteractionDndId(interaction.id)
            )}
            strategy={verticalListSortingStrategy}
            id={`quiz-feedback-${slot}`}
          >
            <div
              ref={setNodeRef}
              className={`min-h-[34px] space-y-0.5 rounded-5 ${
                isBlockedDropTarget
                  ? "cursor-no-drop opacity-50"
                  : isHoverDropTarget
                    ? "ring-1 ring-creator-primary bg-creator-primary-washed"
                    : ""
              }`}
              data-blocked-drop={isBlockedDropTarget ? "true" : undefined}
              data-drop-target={isHoverDropTarget ? "true" : undefined}
            >
              {interactions.length > 0 ? (
                interactions.map((interaction) => (
                  <EditorInteractionRow
                    key={interaction.id}
                    interaction={interaction}
                    feedbackContext={{ takeId, quizId: quiz.id, slot }}
                    testIdPrefix="quiz-feedback-row"
                  />
                ))
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="px-2 py-2 text-[13px] font-normal italic text-creator-text-sub">
                    {t("quiz_feedback_empty")}
                  </div>
                  <ContextualHint hintId={HintId.FeedbackPaste} />
                </div>
              )}
            </div>
          </SortableContext>
          <EditorColumnInlineAddButton
            label={t("add_interaction")}
            onClick={onAddInteraction}
            testId={`quiz-feedback-add-interaction-${slot}`}
          />
        </EditorColumnSection>
      </div>
    </InteractionContextMenu>
  );
}

export default function QuizFeedbackColumn() {
  const { t } = useTranslation();
  const { quiz, parentTakeId, addFeedbackInteraction } =
    useQuizFeedbackColumn();
  const trainingType = useSelector(selectTrainingType);
  const [pickerSlot, setPickerSlot] = useState<QuizFeedbackSlot | null>(null);

  if (!quiz || !isQuizFeedbackEnabled(quiz)) return null;

  const feedbackDefinitions =
    trainingType === TrainingType.Training3D
      ? FeedbackSelectableDefinitions3D
      : FeedbackSelectableDefinitions360;

  const handleAddInteraction = (interaction: Interaction) => {
    if (!pickerSlot) return;
    addFeedbackInteraction(pickerSlot, interaction);
  };

  return (
    <EditorColumn
      testId="quiz-feedback-column"
      widthClassName="w-full min-w-0"
      header={
        <EditorColumnHeader
          icon={<QuizOutlinedIcon fontSize="inherit" />}
          title={t("quiz_feedback")}
          subtitle={quiz.name}
          testId="quiz-feedback-column-header"
        />
      }
    >
      <QuizFeedbackSection
        quiz={quiz}
        takeId={parentTakeId}
        slot="correct"
        title={t("quiz_feedback_correct")}
        icon={<CheckCircleOutlineOutlinedIcon fontSize="inherit" />}
        definitions={feedbackDefinitions}
        onAddInteraction={() => setPickerSlot("correct")}
      />
      <QuizFeedbackSection
        quiz={quiz}
        takeId={parentTakeId}
        slot="wrong"
        title={t("quiz_feedback_wrong")}
        icon={<HighlightOffOutlinedIcon fontSize="inherit" />}
        definitions={feedbackDefinitions}
        onAddInteraction={() => setPickerSlot("wrong")}
      />
      <InteractionPicker
        open={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        definitions={feedbackDefinitions}
        onAddInteraction={handleAddInteraction}
      />
    </EditorColumn>
  );
}
